import * as yup from "yup";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	LinearProgress,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { makeStyles } from "tss-react/mui";
import AddDialogStyle from "styles/application/AddDialogStyle";
import { generateErrorState, handleValidateObj } from "helpers/utils";
import { showError } from "../../../redux/common/actions";
import { useDispatch } from "react-redux";
import ColourConstants from "helpers/colourConstants";

const ADD = AddDialogStyle();
const defaultData = {
	currentPassword: "",
	newPassword: "",
	confirmPassword: "",
};
const defaultError = {
	currentPassword: null,
	newPassword: null,
	confirmPassword: null,
};

const media = "@media (max-width: 414px)";

const useStyles = makeStyles()((theme) => ({
	dialogContent: {
		display: "flex",
		flexDirection: "column",
	},
	createButton: {
		[media]: {
			width: "auto",
		},
	},
	labelText: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: "14px",
		marginBottom: "10px",
	},
	expandIcon: {
		transform: "scale(0.8)",
	},
	inputText: {
		fontSize: 14,
	},
	inputContainer: {
		width: "100%",
		display: "flex",
		flexDirection: "column",
		marginBottom: 20,
	},
}));

const PasswordResetDialog = ({ open, handleClose, apis, id, getError }) => {
	const { t } = useTranslation();
	const { classes, cx } = useStyles();
	const [input, setInput] = useState(defaultData);
	const [errors, setErrors] = useState(defaultError);
	const [loading, setLoading] = useState(false);
	const dispatch = useDispatch();

	const closeOverride = () => {
		handleClose();
		setInput(defaultData);
		setErrors(defaultError);
	};

	const schema = yup.object({
		currentPassword: yup
			.string(t("validation.mustBeString"))
			.required(t("validation.required")),
		newPassword: yup
			.string(t("validation.mustBeString"))
			.required(t("validation.required")),
		confirmPassword: yup
			.string(t("validation.mustBeString"))
			// .oneOf([yup.ref("newPassword")], "Password must be the same!")
			.test("passwords-match", t("validation.passwordsMustMatch"), function (value) {
				return input.newPassword === value;
			})
			.required(t("validation.required")),
	});

	const handlePasswordReset = async () => {
		setLoading(true);
		try {
			const localChecker = await handleValidateObj(schema, input);
			if (!localChecker.some((el) => el.valid === false)) {
				const newData = await resetPassword();
				if (newData.success) {
					setLoading(false);
					closeOverride();
				} else {
					setErrors({ ...errors, ...newData.errors });
					setLoading(false);
				}
			} else {
				const newErrors = generateErrorState(localChecker);
				setErrors({ ...errors, ...newErrors });
				setLoading(false);
			}
		} catch (err) {
			setLoading(false);
			closeOverride();
			dispatch(showError(t("validation.failedToResetPassword")));
		}
	};

	//add
	const resetPassword = async () => {
		try {
			let data = {
				oldPassword: input.currentPassword,
				newPassword: input.newPassword,
				// confirmPassword: input.confirmPassword,
			};

			// Sending create POST to backend
			let result = await apis.postPasswordResetAPI((id = "me"), data);

			if (result.status) {
				return { success: true };
			} else {
				if (result.data.detail) {
					getError(result.data.detail);
					// setErrors({ ...errors, currentPassword: result.data.detail });

					return {
						success: false,
						errors: { currentPassword: result.data.detail },
					};
				} else {
					return { success: false, errors: { ...result.data.errors } };
				}
			}
		} catch (err) {
			throw new Error(err);
		}
	};

	const handleEnterPress = (e) => {
		// 13 is the enter keycode
		if (!loading) {
			if (e.keyCode === 13) {
				handlePasswordReset();
			}
		}
	};

	return (
		<Dialog
			fullWidth={true}
			maxWidth="md"
			open={open}
			onClose={closeOverride}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			className="application-dailog"
		>
			{loading ? <LinearProgress /> : null}
			<ADD.ActionContainer>
				<DialogTitle id="alert-dialog-title">
					{<ADD.HeaderText>{t("title.passwordReset")}</ADD.HeaderText>}
				</DialogTitle>
				<ADD.ButtonContainer>
					<ADD.CancelButton
						onClick={closeOverride}
						variant="contained"
						sx={{
							"&.MuiButton-root:hover": {
								backgroundColor: ColourConstants.deleteDialogHover,
								color: "#ffffff",
							},
						}}
					>
						{t("action.cancel")}
					</ADD.CancelButton>
					<ADD.ConfirmButton
						onClick={handlePasswordReset}
						variant="contained"
						className={classes.createButton}
						disabled={loading}
						sx={{
							"&.MuiButton-root:hover": {
								backgroundColor: ColourConstants.deleteDialogHover,
								color: "#ffffff",
							},
						}}
					>
						{t("action.save")}
					</ADD.ConfirmButton>
				</ADD.ButtonContainer>
			</ADD.ActionContainer>
			<DialogContent className={classes.dialogContent}>
				<div className={classes.inputContainer}>
					<ADD.NameLabel>
						{t("label.currentPassword")}<ADD.RequiredStar>*</ADD.RequiredStar>
					</ADD.NameLabel>
					<TextField
						error={errors.currentPassword === null ? false : true}
						helperText={
							errors.currentPassword === null ? null : errors.currentPassword
						}
						fullWidth
						onChange={(e) =>
							setInput({ ...input, currentPassword: e.target.value })
						}
						onKeyDown={handleEnterPress}
						variant="outlined"
						type="password"
					/>
				</div>
				<div className={classes.inputContainer}>
					<ADD.NameLabel>
						{t("label.newPassword")}<ADD.RequiredStar>*</ADD.RequiredStar>
					</ADD.NameLabel>
					<TextField
						error={errors.newPassword === null ? false : true}
						helperText={errors.newPassword === null ? null : errors.newPassword}
						fullWidth
						onChange={(e) =>
							setInput({ ...input, newPassword: e.target.value })
						}
						onKeyDown={handleEnterPress}
						variant="outlined"
						type="password"
					/>
				</div>
				<div className={classes.inputContainer}>
					<ADD.NameLabel>
						{t("label.confirmPassword")}<ADD.RequiredStar>*</ADD.RequiredStar>
					</ADD.NameLabel>
					<TextField
						error={errors.confirmPassword === null ? false : true}
						helperText={
							errors.confirmPassword === null ? null : errors.confirmPassword
						}
						fullWidth
						onChange={(e) =>
							setInput({ ...input, confirmPassword: e.target.value })
						}
						onKeyDown={handleEnterPress}
						variant="outlined"
						type="password"
					/>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default PasswordResetDialog;
